// Pulso Reach logo — a pulse / heartbeat waveform inside a rounded ink tile
// with the brand's turquoise and yellow corner dots. Keeps the same visual
// rhythm as the previous mark so the rebrand reads as an evolution.
//
// Sizes: 36px (header default), 56px (hero/footer), 80px (cover art).

import { CSSProperties } from "react";

interface LogoProps {
  size?: number;
  className?: string;
  style?: CSSProperties;
}

export function LogoMark({ size = 36, className = "", style }: LogoProps) {
  const dot = Math.max(8, Math.round(size * 0.22));
  const radius = Math.round(size * 0.22);
  return (
    <div
      className={`relative inline-grid place-items-center overflow-visible ${className}`}
      style={{ width: size, height: size, ...style }}
    >
      <div
        className="absolute inset-0 bg-ink-900"
        style={{ borderRadius: radius }}
      />
      <svg
        viewBox="0 0 64 64"
        width={size}
        height={size}
        aria-hidden
        className="relative z-10"
      >
        {/* Pulse waveform — flat → small dip → tall accent peak → flat */}
        <path
          d="M8 34 L20 34 L24 28 L28 40 L32 14 L36 46 L40 30 L44 34 L56 34"
          stroke="white"
          strokeWidth="3.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        {/* Yellow accent dot at the apex of the tall peak */}
        <circle cx="32" cy="14" r="3.4" fill="#f5d547" />
      </svg>
      {/* Corner dots — same composition as the prior mark */}
      <span
        aria-hidden
        className="absolute rounded-full bg-accent-400"
        style={{ width: dot, height: dot, top: -dot / 2, right: -dot / 2 }}
      />
      <span
        aria-hidden
        className="absolute rounded-full bg-brand-400"
        style={{ width: dot, height: dot, bottom: -dot / 2, left: -dot / 2 }}
      />
    </div>
  );
}

// Inline SVG-only variant for places that already manage their own container
// (e.g., the existing rounded ink tile in the marketing/blog/app headers).
// This drops in where the bare "M" letter was, keeping the surrounding tile
// + corner dots untouched.
export function PulseGlyph({ className = "" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 64 64"
      aria-hidden
      className={`relative z-10 h-[70%] w-[70%] ${className}`}
    >
      <path
        d="M8 34 L20 34 L24 28 L28 40 L32 14 L36 46 L40 30 L44 34 L56 34"
        stroke="white"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <circle cx="32" cy="14" r="3.6" fill="#f5d547" />
    </svg>
  );
}
